import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Preferences} from '@capacitor/preferences';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {
  buildLocalizedRoutePathname,
  extractLocalizedRouteLanguageFromUrl,
  extractLocalizedRouteLanguageFromPathname,
  AppLanguageCode,
  DEFAULT_APP_LANGUAGE_CODE,
  normalizeAppLanguageCode,
  isLocalizedPublicRouteUrl,
  SUPPORTED_APP_LANGUAGE_CODES,
} from './app-language';
import {isNativeCapacitorPlatform} from '../platform';

const LANGUAGE_STORAGE_KEY = 'item-tracker.language';

function readNavigatorLanguage(): AppLanguageCode | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  return normalizeAppLanguageCode(navigator.language);
}

@Injectable({
  providedIn: 'root',
})
export class AppLanguageService {
  private currentLanguageCode: AppLanguageCode = DEFAULT_APP_LANGUAGE_CODE;
  private languageActivationId = 0;

  constructor(
    private readonly translateService: TranslateService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  get languageCode(): AppLanguageCode {
    return this.currentLanguageCode;
  }

  async initialize(routeUrl?: string): Promise<void> {
    this.translateService.addLangs([...SUPPORTED_APP_LANGUAGE_CODES]);
    this.translateService.setFallbackLang(DEFAULT_APP_LANGUAGE_CODE);

    const routeLanguage = this.readRouteLanguage(routeUrl);
    if (typeof window === 'undefined') {
      await this.useLanguage(routeLanguage ?? DEFAULT_APP_LANGUAGE_CODE);
      return;
    }

    const storedPreference = await Preferences.get({key: LANGUAGE_STORAGE_KEY});
    const nextLanguageCode =
      routeLanguage
      ?? normalizeAppLanguageCode(storedPreference.value)
      ?? readNavigatorLanguage()
      ?? DEFAULT_APP_LANGUAGE_CODE;

    await this.useLanguage(nextLanguageCode);
  }

  async useLanguage(languageCode: AppLanguageCode): Promise<void> {
    await this.activateLanguage(languageCode, true);
  }

  async activatePublicRouteLanguage(routeUrl: string): Promise<void> {
    if (isNativeCapacitorPlatform()) {
      return;
    }

    const routeLanguage = extractLocalizedRouteLanguageFromUrl(routeUrl);
    if (!routeLanguage) {
      return;
    }

    await this.activateLanguage(routeLanguage, false);
  }

  async useProfileLanguage(value: string | null | undefined): Promise<void> {
    const language = normalizeAppLanguageCode(value);
    if (!language) {
      return;
    }

    const navigation = this.router.getCurrentNavigation();
    const routeUrl = navigation?.extractedUrl.toString()
      ?? (typeof window === 'undefined' ? this.router.url : window.location.href);
    if (!isNativeCapacitorPlatform() && isLocalizedPublicRouteUrl(routeUrl)) {
      await this.activatePublicRouteLanguage(routeUrl);
      return;
    }

    // A guard must not rewrite the previous history entry while navigation is pending.
    await this.activateLanguage(language, navigation === null);
  }

  private async activateLanguage(languageCode: AppLanguageCode, syncBrowserUrl: boolean): Promise<void> {
    const activationId = ++this.languageActivationId;
    // Even a cached current language must supersede a different catalog still loading.
    await firstValueFrom(this.translateService.use(languageCode));

    // ngx-translate keeps the latest requested catalog; mirror that rule in our own state.
    if (activationId !== this.languageActivationId) {
      return;
    }

    this.currentLanguageCode = languageCode;
    this.updateDocumentLanguage(languageCode);
    await this.persistLanguage(languageCode).catch(() => undefined);
    if (syncBrowserUrl && activationId === this.languageActivationId) {
      await this.syncBrowserUrlLanguage(languageCode);
    }
  }

  instant(key: string): string {
    return this.translateService.instant(key);
  }

  private async persistLanguage(languageCode: AppLanguageCode): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    await Preferences.set({
      key: LANGUAGE_STORAGE_KEY,
      value: languageCode,
    });
  }

  private updateDocumentLanguage(languageCode: AppLanguageCode): void {
    this.document.documentElement.lang = languageCode;
  }

  private readRouteLanguage(routeUrl?: string): AppLanguageCode | null {
    if (typeof window === 'undefined') {
      return routeUrl ? extractLocalizedRouteLanguageFromUrl(routeUrl) : extractLocalizedRouteLanguageFromPathname(this.router.url);
    }

    if (isNativeCapacitorPlatform()) {
      return null;
    }

    return extractLocalizedRouteLanguageFromPathname(window.location.pathname);
  }

  private async syncBrowserUrlLanguage(languageCode: AppLanguageCode): Promise<void> {
    if (typeof window === 'undefined' || isNativeCapacitorPlatform()) {
      return;
    }

    const currentRouteLanguage = extractLocalizedRouteLanguageFromPathname(window.location.pathname);
    if (!currentRouteLanguage && languageCode === DEFAULT_APP_LANGUAGE_CODE) {
      return;
    }

    const nextPathname = buildLocalizedRoutePathname(window.location.pathname, languageCode);
    const nextUrl = `${nextPathname}${window.location.search}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (currentUrl === nextUrl) {
      return;
    }

    window.history.replaceState(window.history.state, '', nextUrl);
  }
}
